import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { supabase, Usuario } from "../config/supabase";

interface Empresa {
  id: string;
  nome: string;
  cnpj: string;
  tipo_acesso: string;
  ativo: boolean;
  tipo_comissao?: string;
  cnae_principal?: string;
  data_termino_mandato?: string;
  status?: string;
}

interface AuthContextType {
  user: Usuario | null;
  empresaSelecionada: Empresa | null;
  loading: boolean;
  login: (
    email: string,
    senha: string,
  ) => Promise<{
    success: boolean;
    error?: string;
    requiresPasswordReset?: boolean;
    redirectTo?: string;
  }>;
  logout: () => Promise<void>;
  setEmpresaSelecionada: (empresa: Empresa | null) => void;
  atualizarDataEmpresa: (empresaId: string, novaData: string) => void;
  atualizarDataUsuario: (novaData: string) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Usuario | null>(null);
  const [empresaSelecionada, setEmpresaSelecionada] = useState<Empresa | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  const checkUser = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        // Buscar dados do usuário na tabela cp_usuarios
        const { data: userData, error } = await supabase
          .from("cp_usuarios")
          .select("*")
          .eq("email", session.user.email)
          .eq("ativo", true)
          .single();

        if (error) {
          console.error("Erro ao buscar dados do usuário:", error);
          await supabase.auth.signOut();
        } else if (userData) {
          setUser(userData);

          // Atualizar último acesso
          await supabase
            .from("cp_usuarios")
            .update({ ultimo_acesso: new Date().toISOString() })
            .eq("id", userData.id);
        }
      }
    } catch (error) {
      console.error("Erro ao verificar usuário:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkUser();

    // Adicionar listener para mudanças de autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("🔄 Auth state changed:", event);

      if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
        // Atualizar dados do usuário quando o estado muda
        checkUser();
      } else if (event === "SIGNED_OUT") {
        // Limpar dados quando usuário sai
        setUser(null);
        setEmpresaSelecionada(null);
      } else if (event === "USER_UPDATED") {
        // Recarregar dados quando usuário é atualizado
        checkUser();
      }
    });

    // Cleanup ao desmontar
    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (
    email: string,
    senha: string,
  ): Promise<{
    success: boolean;
    error?: string;
    requiresPasswordReset?: boolean;
    redirectTo?: string;
  }> => {
    try {
      setLoading(true);
      console.log("🔍 Iniciando login para:", email);

      // PRIMEIRO: Tentar fazer login no Supabase Auth
      console.log("🔍 Tentando login no Supabase Auth...");
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email,
          password: senha,
        });

      console.log("🔍 Resultado do login Auth:", { authData, authError });

      if (authError) {
        console.log("❌ Erro no Supabase Auth:", authError.message);

        // Mapear erros específicos do Supabase para mensagens mais amigáveis
        let errorMessage = "Usuário ou senha incorretos.";

        if (authError.message.includes("Invalid login credentials")) {
          errorMessage = "Usuário ou senha incorretos.";
        } else if (authError.message.includes("Email not confirmed")) {
          errorMessage =
            "Email não confirmado. Verifique sua caixa de entrada.";
        } else if (authError.message.includes("Too many requests")) {
          errorMessage =
            "Muitas tentativas de login. Tente novamente em alguns minutos.";
        } else if (authError.message.includes("User not found")) {
          errorMessage = "Usuário não encontrado.";
        }

        return {
          success: false,
          error: errorMessage,
        };
      }

      if (!authData.user) {
        return {
          success: false,
          error: "Erro desconhecido durante o login.",
        };
      }

      console.log("✅ Login Auth bem-sucedido! Buscando dados do usuário...");

      // SEGUNDO: Buscar dados do usuário na cp_usuarios
      const { data: userData, error: userError } = await supabase
        .from("cp_usuarios")
        .select("*")
        .eq("email", email)
        .eq("ativo", true)
        .single();

      console.log("🔍 Resultado da busca na cp_usuarios:", {
        userData,
        userError,
      });

      if (userError || !userData) {
        console.log(
          "⚠️ Usuário não encontrado na cp_usuarios, mas auth funcionou",
        );

        // Se auth funcionou mas não tem dados em cp_usuarios,
        // usar dados básicos do auth
        const basicUserData = {
          id: authData.user.id,
          email: authData.user.email || email,
          nome_completo:
            authData.user.user_metadata?.nome || email.split("@")[0],
          empresa: "Empresa",
          tipo_usuario: "user",
          ativo: true,
          primeiro_acesso: true,
          senha_temporaria: true,
        };

        setUser(basicUserData as any);
        console.log("✅ Login com dados básicos do auth");
        return { success: true };
      }

      // VERIFICAR SE É PRIMEIRO ACESSO OU SENHA TEMPORÁRIA (usando cp_usuarios)
      if (
        userData.primeiro_acesso === true ||
        userData.senha_temporaria === true
      ) {
        console.log(
          "🔑 Primeiro acesso detectado em cp_usuarios - redirecionando para reset de senha",
        );
        console.log("📋 Dados do usuário:", {
          primeiro_acesso: userData.primeiro_acesso,
          senha_temporaria: userData.senha_temporaria,
        });

        // Não setar o user ainda
        setUser(null);
        return {
          success: true,
          requiresPasswordReset: true,
          redirectTo: "/reset-password-first-login",
        };
      }

      // Tudo OK - usar dados completos
      setUser(userData);

      // Atualizar último acesso
      await supabase
        .from("cp_usuarios")
        .update({ ultimo_acesso: new Date().toISOString() })
        .eq("id", userData.id);

      console.log("✅ Login completo bem-sucedido!");
      return { success: true };
    } catch (error) {
      console.error("❌ Erro durante login:", error);
      return {
        success: false,
        error: "Erro interno do sistema. Tente novamente em alguns instantes.",
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setEmpresaSelecionada(null); // Limpar empresa selecionada
    } catch (error) {
      console.error("Erro durante logout:", error);
    }
  };

  const atualizarDataEmpresa = (empresaId: string, novaData: string) => {
    console.log(
      "🔄 Atualizando data da empresa no contexto:",
      empresaId,
      "nova data:",
      novaData,
    );
    if (empresaSelecionada && empresaSelecionada.id === empresaId) {
      console.log(
        "✅ Atualizando empresa selecionada:",
        empresaSelecionada.nome,
      );
      setEmpresaSelecionada({
        ...empresaSelecionada,
        data_termino_mandato: novaData,
      });
    } else {
      console.log(
        "⚠️ Empresa selecionada não corresponde ao ID:",
        empresaSelecionada?.id,
        "vs",
        empresaId,
      );
    }
  };

  const setEmpresaSelecionadaWrapper = (empresa: Empresa | null) => {
    console.log("🔄 setEmpresaSelecionadaWrapper chamado com:", empresa);
    if (empresa) {
      console.log("🔍 Campos específicos ANTES:", {
        id: empresa.id,
        nome: empresa.nome,
        tipo_comissao: empresa.tipo_comissao,
        cnae_principal: empresa.cnae_principal,
        status: empresa.status,
      });
    }
    setEmpresaSelecionada(empresa);
    if (empresa) {
      console.log("🔍 Campos específicos DEPOIS do setEmpresaSelecionada:", {
        id: empresa.id,
        nome: empresa.nome,
        tipo_comissao: empresa.tipo_comissao,
        cnae_principal: empresa.cnae_principal,
        status: empresa.status,
      });
    }
  };

  const atualizarDataUsuario = (novaData: string) => {
    console.log("🔄 Atualizando data do usuário no contexto:", novaData);
    if (user) {
      console.log("✅ Atualizando usuário:", user.nome_completo);
      setUser({
        ...user,
        data_termino_mandato: novaData,
      });
    }
  };

  // LOG para debug - sempre que empresaSelecionada mudar
  useEffect(() => {
    if (empresaSelecionada) {
      console.log(
        "🏢 [AuthContext] Empresa selecionada atualizada:",
        empresaSelecionada,
      );
      console.log("🔍 Campos específicos:", {
        nome: empresaSelecionada.nome,
        tipo_comissao: empresaSelecionada.tipo_comissao,
        cnae_principal: empresaSelecionada.cnae_principal,
        id: empresaSelecionada.id,
        status: empresaSelecionada.status || "ATIVA",
      });
    }
  }, [empresaSelecionada]);

  const value: AuthContextType = {
    user,
    empresaSelecionada,
    loading,
    login,
    logout,
    setEmpresaSelecionada: setEmpresaSelecionadaWrapper,
    atualizarDataEmpresa,
    atualizarDataUsuario,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
